import {
  Component,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  Input,
  SimpleChanges,
  OnChanges,
  Output,
  EventEmitter,
  ChangeDetectionStrategy
} from '@angular/core';
import * as L from 'leaflet';
// Note: avoid dynamic CommonJS `require` at module top-level.
// We'll set global `L` and load markercluster dynamically in the constructor.
import { ZonasRiesgoService } from '/app/mapaRiesgo/services/zonas-riesgo.service';
import { LastPosicion } from '/app/mapaRiesgo/models/lastposicion';
import { NotificationService } from '/app/services/notificacion/notification.service';

@Component({
  selector: 'app-mapa-riesgo',
  standalone: true,
  imports: [],
  templateUrl: './mapa-riesgo.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./mapa-riesgo.component.css'],
})
export class MapaRiesgoComponent
  implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('mapContainer', { static: false })
  mapContainer!: ElementRef<HTMLDivElement>;
  @Input() lastPositions: any[] = [];
  @Input() isRiskEvaluation: boolean = false;
  @Input() alarms: any[] = [];
  @Input() monitorId: string | null = null;
  @Input() markerColor: string = '#172c41';
  @Input() markerRotation: number = 0;
  @Input() filteredAssetIds: string[] | null = null;
  @Input() newAlarmIds: Set<string> | null = null;
  private map?: L.Map;
  private markerClusterGroup?: any;
  private vehicleMarkers: Map<string, { marker: L.Marker; data: any }> =
    new Map();
  private tempMarker?: L.Marker;
  public clusteringEnabled: boolean = true;
  private riskLevelColors: { [key: string]: string } = {
    critical: '#a70000', // Rojo intenso
    high: '#ec0f0f', // Rojo
    medium: '#FF9800', // Naranja
    low: '#FFD700', // Amarillo
  };

  constructor(private zonasRiesgoService: ZonasRiesgoService) {
    // Expose Leaflet globally so the UMD plugin can attach to it
    (window as any).L = L;

    // Cargar leaflet.markercluster de forma dinámica (evita "dynamic require" en runtime bundlers)
    import('leaflet.markercluster')
      .then((mod: any) => {
        try {
          const anyL = (window as any).L || L;
          // El módulo puede exportar la clase como default o como propiedad
          if (!anyL.MarkerClusterGroup) {
            anyL.MarkerClusterGroup = mod?.MarkerClusterGroup ?? mod?.default ?? mod;
          }
          // Debug temporal: informar si MarkerClusterGroup quedó definido
          try { console.debug('[mapa-riesgo] markercluster import: MarkerClusterGroup=', !!anyL.MarkerClusterGroup, anyL.MarkerClusterGroup); } catch (e) {}
        } catch (e) {
          // ignore
        }
      })
      .catch((err) => {
        try { console.debug('[mapa-riesgo] markercluster import failed', err); } catch (e) {}
      });
  }

  /** Genera un ID canónico para un marker: preferir assetId, luego deviceId, luego lat-lng formateado */
  private getMarkerId(lat: number, lng: number, assetId?: any, deviceId?: any): string {
    try {
      if (assetId) return String(assetId);
      if (deviceId) return String(deviceId);
      // Normalizar coordenadas con 6 decimales para evitar desajustes por representación
      return `${Number(lat).toFixed(6)}-${Number(lng).toFixed(6)}`;
    } catch (e) {
      return `${lat}-${lng}`;
    }
  }

  /** Comprueba si un assetId corresponde a una alarma marcada como nueva */
  private isAssetNew(assetId: string | null | undefined): boolean {
    try {
      if (!assetId) return false;
      if (!this.newAlarmIds || !this.alarms) return false;
      for (const a of this.alarms) {
        try {
          if (!a || !a.id) continue;
          if (!this.newAlarmIds.has(a.id)) continue;
          const aid = a.gpsReport?.assetId || a.deviceId || null;
          if (aid && String(aid) === String(assetId)) return true;
        } catch (e) {
          // ignore malformed alarm
        }
      }
    } catch (e) {
      return false;
    }
    return false;
  }

  ngAfterViewInit(): void {
    this.initMap();
    // Cargar zonas después de que el mapa esté listo
    setTimeout(() => {
      if (this.isRiskEvaluation) {
        this.loadAllRiskZones();
      }
      this.scheduleUpdateVehicleMarkers(100);
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Actualizar markers cuando cambien las posiciones
    if (changes['lastPositions']) {
      this.scheduleUpdateVehicleMarkers();
    }

    // Aplicar filtro de markers visibles
    if (changes['filteredAssetIds']) {
      this.applyMarkerFilter();
    }

    // Manejar cambios en isRiskEvaluation
    if (
      changes['isRiskEvaluation'] &&
      !changes['isRiskEvaluation'].firstChange
    ) {
      if (this.isRiskEvaluation) {
        // Cambió a evaluación de riesgo: remover cluster group si existe
        if (this.markerClusterGroup && this.map) {
          this.map.removeLayer(this.markerClusterGroup);
          this.markerClusterGroup = undefined;
        }
        // Cargar zonas si el mapa existe
        if (this.map) {
          this.loadAllRiskZones();
        }
      } else {
        // Cambió a modo normal: crear cluster group
        if (this.map && !this.markerClusterGroup) {
          this.markerClusterGroup = this.createMarkerClusterGroup({
            chunkedLoading: true,
            spiderfyOnMaxZoom: true,
            showCoverageOnHover: false,
            zoomToBoundsOnClick: true,
            removeOutsideVisibleBounds: true,
            animate: true,
            maxClusterRadius: 50, // Reducir el radio de clustering para agrupar más fácilmente
            iconCreateFunction: (cluster: any) =>
              this.createClusterIcon(cluster),
          });
          this.map.addLayer(this.markerClusterGroup);
        }
      }
      // Actualizar markers después del cambio (debounced)
      this.scheduleUpdateVehicleMarkers();
    }

    // Manejar cambios en alarms (sin logs)
  }

  /** Flag para bloquear invalidateSize mientras se navega a una alarma */
  private _navigatingToAlarm = false;
  /** Pending center request queued before the map was ready */
  private pendingCenter: { latitude: number; longitude: number; assetName: string; assetId?: string; color?: string; alarmData?: any } | null = null;

  /**
   * Public API: request a center. If map is not yet initialized, queues the request
   * for processing once initMap() runs.
   */
  public requestCenter(
    latitude: number,
    longitude: number,
    assetName: string,
    assetId?: string,
    color?: string,
    alarmData?: any,
  ): void {
    if (this.map) {
      try {
        this.centerMapOnVehicle(latitude, longitude, assetName, assetId, color, alarmData);
      } catch (e) {
        // ignore
      }
    } else {
      this.pendingCenter = { latitude, longitude, assetName, assetId, color, alarmData };
    }
  }

  centerMapOnVehicle(
    latitude: number,
    longitude: number,
    assetName: string,
    assetId?: string,
    color?: string,
    alarmData?: any,
  ): void {
    // centerMapOnVehicle called (logs removed for production)
    if (!this.map) return;

    // Bloquear invalidateSize durante la operación
    this._navigatingToAlarm = true;

    // 1. Destruir marker temporal anterior
    this.removeTempMarker();


    const maxZoom = 10;
    this.map.setView([latitude, longitude], maxZoom, { animate: false });

    // 3. Crear marker temporal en la posición de la alarma

    // Intentar enriquecer alarmData si está incompleto: buscar en this.alarms o en vehicleMarkers
    let enrichedAlarm: any = null;
    try {
      if (alarmData && (alarmData.eventName || alarmData.location || alarmData.reportDate || alarmData.speed || alarmData.status)) {
        enrichedAlarm = alarmData;
      } else if (assetId && this.alarms && this.alarms.length > 0) {
        enrichedAlarm = this.alarms.find((a: any) => {
          const aid = a?.gpsReport?.assetId || a?.deviceId;
          return aid && String(aid) === String(assetId);
        }) || null;
      }
      if (!enrichedAlarm) {
        const lookupId = this.getMarkerId(latitude, longitude, assetId, alarmData?.deviceId || null);
        const vm = this.vehicleMarkers.get(String(lookupId));
        if (vm && vm.data) enrichedAlarm = vm.data;
      }
    } catch (e) {
      enrichedAlarm = enrichedAlarm || null;
    }

    // centerMapOnVehicle enrichedAlarm computed (logs removed)

    // Helper: intentar encontrar color de alarma por varias fuentes (alarmData, alarms list, lastPositions)
    const findColorFromAlarms = (): string | null => {
      try {
        if (alarmData?.color) return alarmData.color;
        if (enrichedAlarm?.color) return enrichedAlarm.color;
        if (enrichedAlarm?.gpsReport?.color) return enrichedAlarm.gpsReport.color;
        // Buscar por assetId/deviceId en this.alarms
        if (assetId && this.alarms && this.alarms.length > 0) {
          const a = this.alarms.find((x: any) => {
            const aid = x?.gpsReport?.assetId || x?.deviceId;
            return aid && String(aid) === String(assetId);
          });
          if (a) return a.color ?? a.gpsReport?.color ?? null;
        }
        // Buscar por coordenadas en this.alarms
        if (this.alarms && this.alarms.length > 0) {
          const coordMatch = this.alarms.find((x: any) => {
            const latA = x?.gpsReport?.latitude ?? x?.gpsReport?.lat ?? x?.latitude ?? null;
            const lngA = x?.gpsReport?.longitude ?? x?.gpsReport?.lng ?? x?.longitude ?? null;
            if (latA == null || lngA == null) return false;
            const aLat = Number(latA);
            const aLng = Number(lngA);
            return Math.abs(aLat - latitude) < 0.0005 && Math.abs(aLng - longitude) < 0.0005;
          });
          if (coordMatch) return coordMatch.color ?? coordMatch.gpsReport?.color ?? null;
        }
        // Buscar en lastPositions
        if (this.lastPositions && this.lastPositions.length > 0) {
          const lp = this.lastPositions.find((p: any) => {
            const plat = p?.gpsReport?.latitude ?? p?.latitude ?? p?.lat ?? null;
            const plng = p?.gpsReport?.longitude ?? p?.longitude ?? p?.lng ?? null;
            if (plat == null || plng == null) return false;
            return Math.abs(Number(plat) - latitude) < 0.0005 && Math.abs(Number(plng) - longitude) < 0.0005;
          });
          if (lp) return lp.color ?? lp.gpsReport?.color ?? null;
        }
      } catch (e) {
        return null;
      }
      return null;
    };

    const popupColor = color ?? findColorFromAlarms() ?? this.markerColor;

    const eventName = alarmData?.eventName || enrichedAlarm?.gpsReport?.eventName || enrichedAlarm?.eventName || 'Evento';
    const location = alarmData?.location || enrichedAlarm?.gpsReport?.location || enrichedAlarm?.location || 'Sin ubicación';
    const reportDate = alarmData?.reportDate || enrichedAlarm?.gpsReport?.reportDate || enrichedAlarm?.reportDate || 'N/A';
    const speed = alarmData?.speed ?? enrichedAlarm?.gpsReport?.speed ?? enrichedAlarm?.speed;
    const status = alarmData?.status || enrichedAlarm?.status || 'N/A';

    this.tempMarker = L.marker([latitude, longitude], {
      title: `${assetName} — Alarma`,
      icon: this.createTempMarkerIcon(popupColor),
      //icon: this.createDefaultIcon(),
      zIndexOffset: 1000,
    });



    // SVG del camión con el color dinámico incrustado en el popup
    const truckSvg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40" style="margin: 8px auto; display: block;">
        <defs>
          <linearGradient id="truckGrayPopup" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:${popupColor};stop-opacity:1" />
            <stop offset="100%" style="stop-color:${popupColor};stop-opacity:0.6" />
          </linearGradient>
          <filter id="shadowPopup">
            <feDropShadow dx="2" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.4)"/>
          </filter>
        </defs>
        <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" fill="url(#truckGrayPopup)" filter="url(#shadowPopup)" stroke="${popupColor}" stroke-width="0.8" transform="rotate(${this.markerRotation} 12 12)"/>
      </svg>
    `;



    const popupContent = `
      <div style="min-width: 220px; font-size: 12px; background: linear-gradient(135deg, #172c41 0%, #172c41 100%); color: white; padding: 12px; border-radius: 8px;">
        <strong style="font-size: 14px; display: block; margin-bottom: 8px; color: #fff; text-transform: uppercase;">${assetName}</strong>
        <hr style="margin: 5px 0; border: 1px solid rgba(255,255,255,0.3);">
        <b>Asset ID:</b> <span style="color: #90ee90;">${assetId || 'N/A'}</span><br>
        <b>Evento:</b> ${eventName}<br>
        <b>Estado:</b> ${status}<br>
        <b>Ubicación:</b> ${location}<br>
        ${speed != null ? '<b>Velocidad:</b> <span style=\"color: #ffed4e;\">' + speed + '</span> km/h<br>' : ''}
        <b>Fecha reporte:</b> ${this.formatDate(reportDate)}<br>
        <b>Coordenadas:</b> ${latitude.toFixed(4)}, ${longitude.toFixed(4)}
        <hr style="margin: 8px 0; border: 1px solid rgba(255,255,255,0.3);">
        ${truckSvg}
      </div>
    `;
    this.tempMarker.bindPopup(popupContent);
    this.tempMarker.addTo(this.map);

    // Si ya existe un marker persistente para este asset, actualizamos su icono y abrimos su popup
    try {
      const lookupId = this.getMarkerId(latitude, longitude, assetId, alarmData?.deviceId || enrichedAlarm?.gpsReport?.deviceId);
      // lookupId computed for centerMapOnVehicle (logs removed)
      const existing = this.vehicleMarkers.get(String(lookupId));
      if (existing && existing.marker) {
        // Preferir color recibido (_color) o datos enriquecidos; actualizar existing.data
        const newColor = color ?? (enrichedAlarm?.color || enrichedAlarm?.gpsReport?.color) ?? existing.data.color ?? this.markerColor;
        existing.data.color = newColor;
        existing.data.heading = existing.data.heading ?? alarmData?.heading ?? enrichedAlarm?.gpsReport?.heading ?? existing.data.heading;
        const isNewAsset = this.isAssetNew(existing.data.assetId || lookupId);
        // Actualizar icon sin remover/re-agregar al cluster para evitar recarga visual
        try {
          existing.marker.setIcon(this.createDefaultIcon(newColor, existing.data.heading, isNewAsset));
        } catch (e) {
          // fallback silencioso
        }

        // Actualizar popup del marker persistente con datos enriquecidos
        try {
          const evName = enrichedAlarm?.gpsReport?.eventName || enrichedAlarm?.eventName || alarmData?.eventName || existing.data.eventName || 'Evento';
          const loc = enrichedAlarm?.gpsReport?.location || enrichedAlarm?.location || alarmData?.location || existing.data.location || 'Sin ubicación';
          const repDate = enrichedAlarm?.gpsReport?.reportDate || enrichedAlarm?.reportDate || alarmData?.reportDate || existing.data.reportDate || 'N/A';
          const spd = alarmData?.speed ?? enrichedAlarm?.gpsReport?.speed ?? enrichedAlarm?.speed ?? existing.data.speed;

          const persistentPopup = `
            <div style="min-width: 220px; font-size: 12px; background: linear-gradient(135deg, #172c41 0%, #172c41 100%); color: white; padding: 12px; border-radius: 8px; ">
              <strong style="font-size: 14px; display: block; margin-bottom: 8px; color: #fff; text-transform: uppercase;">${existing.data.assetName || assetName}</strong>
              <hr style="margin: 5px 0; border: 1px solid rgba(255,255,255,0.3);">
              <b>Asset ID:</b> <span style="color: #90ee90;">${existing.data.assetId || assetId || 'N/A'}</span><br>
              <b>Evento:</b> ${evName}<br>
              <b>Ubicación:</b> ${loc}<br>
              ${spd != null ? '<b>Velocidad:</b> <span style="color: #ffed4e;">' + spd + '</span> km/h<br>' : ''}
              <b>Coordenadas:</b> ${latitude.toFixed(4)}, ${longitude.toFixed(4)}<br>
              <b>Fecha reporte:</b> ${this.formatDate(repDate)}
            </div>
          `;
          existing.marker.unbindPopup?.();
          existing.marker.bindPopup(persistentPopup);
          try { existing.marker.openPopup?.(); } catch (e) {}
        } catch (e) {
          // ignore popup errors
        }
      }
    } catch (e) {
      // ignore
    }

    // tempMarker popup content (silenciado)

    // 4. Abrir popup tras breve pausa (el mapa ya está centrado)
    setTimeout(() => {
      this.tempMarker?.openPopup();

      // Esperar un poco más para que el popup esté renderizado
      setTimeout(() => {
        if (this.tempMarker) {
          const popupElement = this.tempMarker.getPopup()?.getElement();
          if (popupElement) {
            // Buscar el wrapper del popup y aplicar el color con !important
            const wrapper = popupElement.querySelector(
              '.leaflet-popup-content-wrapper',
            ) as HTMLElement;
            const tip = popupElement.querySelector(
              '.leaflet-popup-tip',
            ) as HTMLElement;

            if (wrapper) {
              wrapper.style.setProperty(
                'background-color',
                '#172c41',
                'important',
              );
              wrapper.style.setProperty('border-color', '#172c41', 'important');
              wrapper.style.setProperty('background', '#172c41', 'important');
            }
            if (tip) {
              tip.style.setProperty('border-top-color', '#172c41', 'important');
            }
          }
        }
        // Desbloquear invalidateSize después de un breve período de protección
        this._navigatingToAlarm = false;
      }, 150);
    }, 200);
  }

  /** Crea un icono de marker temporal con el color de la alarma (mismo estilo tractocamión) */
  private createTempMarkerIcon(color: string): L.Icon {

    let svgIcon: string;

    if (this.isRiskEvaluation) {
      // Icono de marcador para evaluación de riesgo
      svgIcon = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="25" height="41">
          <defs>
            <linearGradient id="truckGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:${color};stop-opacity:1" />
              <stop offset="100%" style="stop-color:${color};stop-opacity:1" />
            </linearGradient>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="1" dy="1" stdDeviation="1" flood-color="rgba(0,0,0,0.3)"/>
            </filter>
          </defs>
          <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" fill="url(#truckGradient)" filter="url(#shadow)" stroke="#0d5fa6" stroke-width="0.5" transform="rotate(${this.markerRotation} 12 12)"/>
        </svg>
      `;



    } else {
      // Icono de tractocamión para modo normal
      svgIcon = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="25" height="41">
          <defs>
            <linearGradient id="truckGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:${color};stop-opacity:1" />
              <stop offset="100%" style="stop-color:${color};stop-opacity:1" />
            </linearGradient>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="1" dy="1" stdDeviation="1" flood-color="rgba(0,0,0,0.3)"/>
            </filter>
          </defs>
          <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" fill="url(#truckGradient)" filter="url(#shadow)" stroke="#0d5fa6" stroke-width="0.5" transform="rotate(${this.markerRotation} 12 12)"/>
        </svg>
      `;
    }

    return L.icon({
      iconUrl: `data:image/svg+xml;base64,${btoa(svgIcon)}`,
      iconSize: [30, 48],
      iconAnchor: [15, 48],
      popupAnchor: [1, -40],
      shadowUrl:
        'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
      shadowSize: [41, 41],
    });
  }

  /** Elimina el marker temporal si existe */
  private removeTempMarker(): void {
    if (this.tempMarker) {
      this.map?.removeLayer(this.tempMarker);
      this.tempMarker = undefined;
    }
  }

  /** Formatea fechas para mostrar en popups (ej: 25/03/2026 14:30) */
  private formatDate(value: any): string {
    if (value === null || value === undefined) return 'N/A';
    // Si ya es Date
    let d: Date;
    if (value instanceof Date) {
      d = value;
    } else {
      // Intentar parsear cadenas/num
      d = new Date(value);
    }
    if (isNaN(d.getTime())) {
      // No es una fecha válida: devolver el valor original como fallback
      return String(value) || 'N/A';
    }
    try {
      return new Intl.DateTimeFormat('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(d);
    } catch (e) {
      // Fallback simple
      return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
        .toString()
        .padStart(2, '0')}/${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;
    }
  }

  /** Muestra/oculta markers según los IDs filtrados */
  private applyMarkerFilter(): void {
    if (!this.map) return;

    const filterSet = this.filteredAssetIds
      ? new Set(this.filteredAssetIds)
      : null;

    this.vehicleMarkers.forEach((markerData, assetId) => {
      const marker = markerData.marker;
      const shouldShow = !filterSet || filterSet.has(assetId);

      if (shouldShow) {
        // Si está en cluster, el cluster se encarga; si no, añadir al mapa
        if (this.clusteringEnabled && this.markerClusterGroup) {
          if (!this.markerClusterGroup.hasLayer(marker)) {
            this.markerClusterGroup.addLayer(marker);
          }
        } else {
          if (!this.map!.hasLayer(marker)) {
            marker.addTo(this.map!);
          }
        }
      } else {
        // Ocultar marker
        if (this.clusteringEnabled && this.markerClusterGroup) {
          this.markerClusterGroup.removeLayer(marker);
        } else {
          this.map!.removeLayer(marker);
        }
      }
    });
  }

  private createDefaultIcon(color?: string, heading?: number, isNew: boolean = false): L.Icon {


    // const headingTotal = 360 - (heading ?? this.markerRotation);
    const headingTotal = 0;

    const useColor = color ?? this.markerColor;
    let svgIcon: string;

    if (this.isRiskEvaluation) {
      // Icono de marcador para evaluación de riesgo
      svgIcon = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="25" height="41">
          <defs>
            <linearGradient id="truckGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:${useColor};stop-opacity:1" />
              <stop offset="100%" style="stop-color:${useColor};stop-opacity:1" />
            </linearGradient>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="1" dy="1" stdDeviation="1" flood-color="rgba(0,0,0,0.3)"/>
            </filter>
          </defs>
          <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" fill="url(#truckGradient)" filter="url(#shadow)" stroke="#0d5fa6" stroke-width="0.5" transform="rotate(${headingTotal} 12 12)"/>
        </svg>
      `;
    } else {
      // Icono de tractocamión para modo normal
      svgIcon = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="25" height="41">
          <defs>
            <linearGradient id="truckGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:${useColor};stop-opacity:1" />
              <stop offset="100%" style="stop-color:${useColor};stop-opacity:1" />
            </linearGradient>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="1" dy="1" stdDeviation="1" flood-color="rgba(0,0,0,0.3)"/>
            </filter>
          </defs>
          <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" fill="url(#truckGradient)" filter="url(#shadow)" stroke="#0d5fa6" stroke-width="0.5" transform="rotate(${headingTotal} 12 12)"/>
        </svg>
      `;
    }

    // Si es nueva alarma, envolver el SVG en un anillo exterior amarillo
    let finalSvg = svgIcon;
    if (isNew) {
      const ring = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"><circle cx="24" cy="24" r="22" fill="none" stroke="#ffeb3b" stroke-width="3" /></svg>`;
      // combinar ring y icon en un solo SVG con overlay (simple concatenación dentro de un <g>)
      finalSvg = `<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">` +
        `<g transform="translate(9,1)">` + svgIcon.replace(/^\s*<\?xml[^>]*>\s*/,'') + `</g>` +
        `<circle cx="24" cy="24" r="22" fill="none" stroke="#ffeb3b" stroke-width="3" />` +
        `</svg>`;
    }

    return L.icon({
      iconUrl: `data:image/svg+xml;base64,${btoa(finalSvg)}`,
      iconSize: isNew ? [48, 48] : [30, 48],
      iconAnchor: isNew ? [24, 48] : [15, 48],
      popupAnchor: [1, -40],
      shadowUrl:
        'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
      shadowSize: [41, 41],
    });
  }

  private createClusterIcon(cluster: any): L.Icon {
    // Creating cluster icon (log removed)
    const childCount = cluster.getChildCount();
    const color = '#172c41';
    let size: number;
    let fontSize: number;

    // Determinar tamaño y clase basado en el número de marcadores
    if (childCount < 5) {
      size = 45;
      fontSize = 13;
    } else if (childCount < 15) {
      size = 55;
      fontSize = 15;
    } else if (childCount < 30) {
      size = 65;
      fontSize = 17;
    } else {
      size = 75;
      fontSize = 19;
    }

    // Crear icono SVG simplificado para el cluster
    const textSize = Math.max(12, Math.round(fontSize * 1.6));
    const textY = Math.round(size / 2 + textSize / 4);
    const svgIcon =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
      `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${color}" stroke="white" stroke-width="3"/>` +
      `<text x="${size / 2}" y="${textY}" text-anchor="middle" fill="#ffffff" font-family="Roboto,sans-serif" font-size="${textSize}px" font-weight="700" stroke="rgba(0,0,0,0.35)" stroke-width="1">${childCount}</text>` +
      `</svg>`;


    // const  svgIcon = `
    //   <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="25" height="41">
    //     <defs>
    //       <linearGradient id="truckGradient" x1="0%" y1="0%" x2="100%" y2="100%">
    //         <stop offset="0%" style="stop-color:${color};stop-opacity:1" />
    //         <stop offset="100%" style="stop-color:${color};stop-opacity:1" />
    //       </linearGradient>
    //       <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
    //         <feDropShadow dx="1" dy="1" stdDeviation="1" flood-color="rgba(0,0,0,0.3)"/>
    //       </filter>
    //     </defs>
    //     <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" fill="url(#truckGradient)" filter="url(#shadow)" stroke="#0d5fa6" stroke-width="0.5" transform="rotate(0 12 12)"/>
    //   </svg>
    // `;



    return L.icon({
      iconUrl: `data:image/svg+xml;base64,${btoa(svgIcon)}`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2],
    });
  }

  private createMarkerClusterGroup(options: any): any {
    const anyL = L as any;
    try {
      if (typeof anyL.markerClusterGroup === 'function') {
        return anyL.markerClusterGroup(options);
      }
      if (typeof anyL.MarkerClusterGroup === 'function') {
        return new anyL.MarkerClusterGroup(options);
      }
    } catch (e) {
      // Error creando marker cluster group (silenciado)
    }
    // leaflet.markercluster no encontrado (silenciado)
    return L.layerGroup();
  }

  private loadAllRiskZones(): void {
    // Cargar todas las zonas de riesgo
    const riskLevels = ['critical', 'high', 'medium', 'low'];
    let loadedCount = 0;

    riskLevels.forEach((level) => {
      this.zonasRiesgoService.getZonas(level).subscribe({
        next: (data) => {
          if (data && data.features) {
            // Pintar cada geoforma conforme se carga con su riskLevel
            data.features.forEach((feature: any) => {
              this.paintGeoformaOnMap(feature, level);
            });
          }
          loadedCount++;
          // if (loadedCount === riskLevels.length) {
          //   Zonas de riesgo cargadas (silenciado)
          // }
        },
        error: (err) => {
          // Error cargando zonas de riesgo (silenciado)
          loadedCount++;
        },
      });
    });
  }

  /** Debounce para evitar ejecuciones duplicadas de updateVehicleMarkers en ráfaga */
  private scheduledUpdateTimer: any = null;
  private scheduleUpdateVehicleMarkers(delay: number = 50): void {
    if (this.scheduledUpdateTimer) {
      clearTimeout(this.scheduledUpdateTimer);
    }
    this.scheduledUpdateTimer = setTimeout(() => {
      this.scheduledUpdateTimer = null;
      try {
        this.updateVehicleMarkers();
      } catch (e) {
        // ignore
      }
    }, delay);
  }

  private paintGeoformaOnMap(feature: any, riskLevel: string): void {
    if (!this.map) return;

    const color =
      this.riskLevelColors[riskLevel] ||
      this.riskLevelColors['low'] ||
      '#cf1616';

    const geoJsonLayer = L.geoJSON(feature, {
      style: () => ({
        color: color,
        fillColor: color,
        weight: 2,
        opacity: 0.8,
        fillOpacity: 0.5,
      }),
      onEachFeature: (feature: any, layer: any) => {
        const props = feature.properties || {};
        const popupContent = `
          <div style="font-size: 12px;">
            <strong>Zona de Riesgo</strong><br>
            Riesgo: <span style="color: ${color};"><strong>${riskLevel || 'N/A'}</strong></span><br>          
            Área: ${props.area_km2?.toFixed(2) || 'N/A'} km²
          </div>
        `;
        layer.bindPopup(popupContent);
      },
    });

    geoJsonLayer.addTo(this.map);
  }

  private initMap(): void {
    const el =
      this.mapContainer?.nativeElement ||
      (document.getElementById('map') as HTMLElement);
    this.map = L.map(el).setView([23.6345, -102.5528], 6); // Centered on Mexico);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(this.map);

    // Process any pending center request queued before the map was ready
    if (this.pendingCenter) {
      const p = this.pendingCenter;
      this.pendingCenter = null;
      setTimeout(() => {
        try {
          this.centerMapOnVehicle(p.latitude, p.longitude, p.assetName, p.assetId, p.color, p.alarmData);
        } catch (e) {
          // ignore
        }
      }, 50);
    }

    // Inicializar clustering solo si no es evaluación de riesgo y clustering está habilitado
    if (!this.isRiskEvaluation && this.clusteringEnabled) {
      // Intentar cargar el plugin de markercluster y crear el grupo cuando esté listo.
      import('leaflet.markercluster')
        .then((mod: any) => {
          try {
            const anyL = (window as any).L || L;
            if (!anyL.MarkerClusterGroup) {
              anyL.MarkerClusterGroup = mod?.MarkerClusterGroup ?? mod?.default ?? mod;
            }
            this.markerClusterGroup = this.createMarkerClusterGroup({
              chunkedLoading: true,
              spiderfyOnMaxZoom: true,
              showCoverageOnHover: false,
              zoomToBoundsOnClick: true,
              removeOutsideVisibleBounds: true,
              animate: true,
              maxClusterRadius: 50, // Reducir el radio de clustering para agrupar más fácilmente
              iconCreateFunction: (cluster: any) =>
                this.createClusterIcon(cluster),
            });
            this.map!.addLayer(this.markerClusterGroup);
            try { console.debug('[mapa-riesgo] initMap: created markerClusterGroup, isLayerGroup=', this.markerClusterGroup instanceof L.LayerGroup, 'hasAddLayer=', typeof this.markerClusterGroup.addLayer === 'function'); } catch (e) {}
            // Re-procesar markers en caso de que ya se hayan añadido antes de que el plugin cargara
            this.scheduleUpdateVehicleMarkers();
          } catch (e) {
            // Error inicializando MarkerCluster después de import (silenciado)
            this.markerClusterGroup = L.layerGroup();
            this.map!.addLayer(this.markerClusterGroup);
          }
        })
        .catch((err) => {
          // leaflet.markercluster no disponible, usando LayerGroup (silenciado)
          this.markerClusterGroup = L.layerGroup();
          this.map!.addLayer(this.markerClusterGroup);
        });
    }
  }

  ngOnDestroy(): void {
    this.removeTempMarker();
    if (this.map) {
      this.map.remove();
    }
  }

  private clearVehicleMarkers(): void {
    // NO limpiar el temp marker aquí — se destruye solo al seleccionar otra alarma
    this.vehicleMarkers.forEach((markerData) => {
      if (this.markerClusterGroup) {
        this.markerClusterGroup.removeLayer(markerData.marker);
      } else {
        this.map?.removeLayer(markerData.marker);
      }
    });
    this.vehicleMarkers.clear();
  }

  private updateVehicleMarkers(): void {

    // Evitar llamadas redundantes (debounce manejado externamente)

    if (!this.map) return;

    if (!this.lastPositions || this.lastPositions.length === 0) {
      this.clearVehicleMarkers();
      return;
    }

    this.clearVehicleMarkers();

    // Crear nuevos markers
    this.lastPositions.forEach((rawPosition: any) => {
      // Log de cada posición recibida para crear marker
      // Procesando rawPosition para marker (silenciado)
      const gps = rawPosition?.gpsReport || {};
      const position = { ...gps, ...rawPosition };

      const latitudeValue = position.latitude ?? position.lat;
      const longitudeValue = position.longitude ?? position.lng;

      if (
        latitudeValue !== undefined &&
        latitudeValue !== null &&
        longitudeValue !== undefined &&
        longitudeValue !== null
      ) {
        const lat = parseFloat(String(latitudeValue));
        const lng = parseFloat(String(longitudeValue));

        // Verificar coordenadas válidas
        if (isNaN(lat) || isNaN(lng)) return;



        // Crear marker con ícono personalizado (usar ID canónico)
        const markerId = this.getMarkerId(lat, lng, position.assetId, position.deviceId);

        // Derivar assetIds correspondientes a alarmas nuevas (parent provee alarm IDs)
        const newAlarmAssetIds = new Set<string>();
        if (this.newAlarmIds && this.alarms && this.alarms.length > 0) {
          this.alarms.forEach((a: any) => {
            try {
              if (this.newAlarmIds && this.newAlarmIds.has(a.id)) {
                const aid = a.gpsReport?.assetId || a.deviceId || null;
                if (aid) newAlarmAssetIds.add(aid);
              }
            } catch (e) {
              // ignore malformed alarm
            }
          });
        }

        // Considerar nueva si la set contiene el assetId, o si el parent ya pasó assetIds directamente
        const isNew = !!(
          (this.newAlarmIds && this.newAlarmIds.has(markerId)) ||
          newAlarmAssetIds.has(markerId)
        );

        // Marker creado (detalles omitidos)

        const marker = L.marker([lat, lng], {
          title: position.assetName || 'Vehículo',
          icon: this.createDefaultIcon(position.color, position.heading, isNew),
        });

        // Crear popup con información del vehículo
        const popupContent = `
          <div style="min-width: 220px; font-size: 12px; background: linear-gradient(135deg, #172c41 0%, #172c41 100%); color: white; padding: 12px; border-radius: 8px; ">
            <strong style="font-size: 14px; display: block; margin-bottom: 8px; color: #fff; text-transform: uppercase;">${position.assetName || 'Vehículo'}</strong>
            <hr style="margin: 5px 0; border: 1px solid rgba(255,255,255,0.3);">
            <b>Asset ID:</b> <span style="color: #90ee90;">${position.assetId || 'N/A'}</span><br>
            <b>Evento:</b> ${position.eventName || 'N/A'}<br>
            <b>Ubicación:</b> ${position.location || 'N/A'}<br>
            <b>Velocidad:</b> <span style="color: #ffed4e;">${position.speed || 'N/A'}</span> km/h<br>
            <b>Rumbo:</b> ${position.heading || 'N/A'}°<br>
            <b>Coordenadas:</b> ${lat.toFixed(4)}, ${lng.toFixed(4)}<br>
            <b>Actualizado:</b> ${this.formatDate(position.inputDate)}
          </div>
        `;

        marker.bindPopup(popupContent);

        // Agregar marker al mapa o cluster group
        if (this.clusteringEnabled && this.markerClusterGroup) {
          this.markerClusterGroup.addLayer(marker);
        } else {
          marker.addTo(this.map!);
        }

        this.vehicleMarkers.set(markerId, { marker, data: position });
      }
    });


  }

  // Método público para ajustar el tamaño del mapa (útil cuando cambia el contenedor)
  public invalidateSize(): void {
    if (this.map) {
      setTimeout(() => {
        // No invalidar si estamos navegando a una alarma — interrumpe el centrado
        if (this._navigatingToAlarm) return;
        this.map?.invalidateSize();
      }, 100);
    }
  }

  // Método público para forzar la actualización de markers desde componentes padres
  public refreshMarkers(): void {
    try {
      this.scheduleUpdateVehicleMarkers();
      setTimeout(() => this.invalidateSize(), 150);
    } catch (e) {
      // Silenciar errores para no afectar al padre
    }
  }

  /**
   * Activa o desactiva el clustering de markers.
   * Por defecto el clustering está activado.
   * @param enabled true para activar clusters, false para desactivarlos
   */
  public toggleClustering(enabled?: boolean): void {
    // Si no se pasa parámetro, alternar el estado actual
    this.clusteringEnabled =
      enabled !== undefined ? enabled : !this.clusteringEnabled;

    if (!this.map) return;

    // Remover todos los markers actuales del mapa/cluster
    this.vehicleMarkers.forEach((markerData) => {
      if (this.markerClusterGroup) {
        this.markerClusterGroup.removeLayer(markerData.marker);
      } else {
        this.map?.removeLayer(markerData.marker);
      }
    });

    // Remover el cluster group actual del mapa si existe
    if (this.markerClusterGroup && this.map) {
      this.map.removeLayer(this.markerClusterGroup);
      this.markerClusterGroup = undefined;
    }

    if (this.clusteringEnabled) {
      // Activar clustering: intentar importar el plugin antes de crear el grupo
      import('leaflet.markercluster')
        .then((mod: any) => {
          try {
            const anyL = (window as any).L || L;
            if (!anyL.MarkerClusterGroup) {
              anyL.MarkerClusterGroup = mod?.MarkerClusterGroup ?? mod?.default ?? mod;
            }
            this.markerClusterGroup = this.createMarkerClusterGroup({
              chunkedLoading: true,
              spiderfyOnMaxZoom: true,
              showCoverageOnHover: false,
              zoomToBoundsOnClick: true,
              removeOutsideVisibleBounds: true,
              animate: true,
              maxClusterRadius: 50,
              iconCreateFunction: (cluster: any) => this.createClusterIcon(cluster),
            });
            if (this.map && this.markerClusterGroup) this.map.addLayer(this.markerClusterGroup);
            try { console.debug('[mapa-riesgo] toggleClustering: created markerClusterGroup, isLayerGroup=', this.markerClusterGroup instanceof L.LayerGroup, 'hasAddLayer=', typeof this.markerClusterGroup.addLayer === 'function'); } catch (e) {}
          } catch (e) {
            // Fallback a LayerGroup
            this.markerClusterGroup = L.layerGroup();
            if (this.map && this.markerClusterGroup) this.map.addLayer(this.markerClusterGroup);
          }

          // Re-agregar todos los markers al nuevo contenedor cuando esté listo
          this.vehicleMarkers.forEach((markerData) => {
            if (this.clusteringEnabled && this.markerClusterGroup) {
              this.markerClusterGroup.addLayer(markerData.marker);
            } else {
              markerData.marker.addTo(this.map!);
            }
          });
        })
        .catch(() => {
          // Si la import falla, usar LayerGroup como fallback y agregar markers
          this.markerClusterGroup = L.layerGroup();
          if (this.map && this.markerClusterGroup) this.map.addLayer(this.markerClusterGroup);
          this.vehicleMarkers.forEach((markerData) => {
            if (this.clusteringEnabled && this.markerClusterGroup) {
              this.markerClusterGroup.addLayer(markerData.marker);
            } else {
              markerData.marker.addTo(this.map!);
            }
          });
        });
      return;
    }

    // Re-agregar todos los markers al nuevo contenedor (no-cluster case)
    this.vehicleMarkers.forEach((markerData) => {
      markerData.marker.addTo(this.map!);
    });
  }
}
